import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    withStyles,
} from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';
import { axiosInstance, cancelToken } from '../../utils/axiosInstance';
import {
    fwPromotionCopy as copy,
    endpoint,
    fwCapabilityReasonLabels,
    fwPairNextStage,
    fwPairStageLabels,
    snackbarDefaultValue,
    snackbarVariant,
} from '../../utils/constants';
import { formatFwDate } from '../../utils/fwHelpers';
import {
    fieldErrors,
    isCancelled,
    isDisabledRefusal,
    isValidRevision,
    normalizeRevision,
    operationKey,
    probeWorkflow,
    refusalOf,
    refusalText,
    workflowModes,
} from '../../utils/fwWorkflow';
import Snackbar from '../Snackbar/Snackbar';

const styles = {
    summary: { marginBottom: '1rem' },
    addRow: { display: 'flex', gap: '1rem', alignItems: 'flex-start', marginTop: '1rem' },
    revisionInput: { width: '18rem' },
    banner: { marginTop: '1rem' },
    promotion: { marginTop: '1rem' },
    table: { overflowX: 'auto' },
};
const positiveInteger = value => Number.isSafeInteger(value) && value > 0;
const counter = value => Number.isSafeInteger(value) && value >= 0;
const identifier = value => {
    if (positiveInteger(value)) return String(value);
    return typeof value === 'string' && /^[1-9]\d*$/.test(value) ? value : null;
};
const pairsOf = response => {
    const rows = response?.data?.data ?? response?.data;
    return Array.isArray(rows) ? rows : null;
};
const promotionState = (pair, settings, fileId) => {
    if (pair.retired_at) return { eligible: false, text: copy.retired };
    if (pair.stage === 'senior') return { eligible: false, text: copy.terminal };
    const target = fwPairNextStage[pair.stage];
    const threshold = settings?.[`${pair.stage}_to_${target}_threshold`];
    const progress = `${copy.confirmed} ${pair.confirmed_count}, ${copy.outstanding} ${pair.outstanding_count}`;
    const prefix = `${copy.progress + (fwPairStageLabels[target] ?? '')}: `;
    if (!target) return { eligible: false, text: copy.invalid };
    if (!positiveInteger(threshold))
        return { eligible: false, text: `${prefix + copy.missingThreshold}. ${progress}.` };
    if (
        !identifier(pair.id) ||
        identifier(pair.fw_file_id) !== fileId ||
        !positiveInteger(pair.version) ||
        !positiveInteger(pair.stage_epoch) ||
        !counter(pair.confirmed_count) ||
        !counter(pair.outstanding_count)
    ) {
        return { eligible: false, text: copy.invalid };
    }
    const thresholdText = `${prefix}próg ${threshold} ${copy.thresholdCount}`;
    if (pair.confirmed_count < threshold)
        return { eligible: false, text: `${thresholdText}, ${progress} — ${copy.below}.` };
    return {
        eligible: true,
        target,
        threshold,
        text: `${thresholdText}, ${progress} — ${copy.thresholdMet}.`,
    };
};
const promotionRefusal = error => {
    const refusal = refusalOf(error);
    const reason = error?.response?.data?.data?.reason ?? refusal.fieldCode;
    return copy.reasons[reason] ?? (typeof reason === 'string' ? reason : refusalText(refusal, copy.unknown));
};

const FwFileSources = ({ classes, file, open, onClose }) => {
    const [pairs, setPairs] = useState([]);
    const [settings, setSettings] = useState(null);
    const [mode, setMode] = useState(workflowModes.LOADING);
    const [newRevision, setNewRevision] = useState('');
    const [errors, setErrors] = useState({});
    const [banner, setBanner] = useState(null);
    const [outcome, setOutcome] = useState(null);
    const [promotion, setPromotion] = useState(null);
    const [phase, setPhase] = useState('editing');
    const [busy, setBusy] = useState(null);
    const [snackbarData, setSnackbarData] = useState(snackbarDefaultValue);
    const addKey = useRef(operationKey());
    const generation = useRef(0);
    const readSequence = useRef(0);
    const requestSource = useRef(null);
    const mutationSource = useRef(null);
    const mutationOwner = useRef(null);
    const frozenPromotion = useRef(null);
    const latest = useRef(null);
    const fileId = identifier(file?.id);
    latest.current = { open, fileId };

    const current = owner =>
        generation.current === owner.generation &&
        latest.current.open &&
        latest.current.fileId === owner.fileId;
    const showMessage = (message, variant) =>
        setSnackbarData({ isSnackbarOpen: true, message, variant, errorInfo: '' });
    const handleIsSnackbarOpen = () => setSnackbarData(snackbarDefaultValue);

    const load = async () => {
        if (mutationOwner.current || !latest.current.open) return;
        const owner = { generation: generation.current, fileId: latest.current.fileId };
        const sequence = ++readSequence.current;
        const active = () => current(owner) && sequence === readSequence.current;
        requestSource.current?.cancel();
        requestSource.current = cancelToken.source();
        const config = { cancelToken: requestSource.current.token };
        frozenPromotion.current = null;
        setPromotion(null);
        setPhase('editing');
        setPairs([]);
        setSettings(null);
        setBanner(null);
        setMode(workflowModes.LOADING);
        if (!owner.fileId) {
            setMode(workflowModes.ERROR);
            setBanner(copy.invalid);
            return;
        }
        const probe = await probeWorkflow(config);
        if (!active()) return;
        if (probe.mode !== workflowModes.CONFIGURED) {
            setMode(probe.mode);
            setBanner(refusalText(probe.refusal, copy.readError));
            return;
        }
        try {
            const response = await axiosInstance.get(`${endpoint.FW_FILES}/${owner.fileId}/sources`, config);
            if (!active()) return;
            const rows = pairsOf(response);
            if (!rows) throw new Error(copy.readError);
            setSettings(probe.settings);
            setPairs(rows);
            setMode(workflowModes.CONFIGURED);
        } catch (error) {
            if (!active() || isCancelled(error)) return;
            setMode(workflowModes.ERROR);
            setBanner(refusalText(refusalOf(error), copy.readError));
        }
    };

    useEffect(() => {
        generation.current += 1;
        mutationOwner.current = null;
        frozenPromotion.current = null;
        setBusy(null);
        setPromotion(null);
        setOutcome(null);
        setSnackbarData(snackbarDefaultValue);
        setNewRevision('');
        setErrors({});
        setPairs([]);
        setSettings(null);
        addKey.current = operationKey();
        if (open) load();
        return () => {
            generation.current += 1;
            requestSource.current?.cancel();
            mutationSource.current?.cancel();
            mutationOwner.current = null;
            frozenPromotion.current = null;
        };
    }, [open, fileId]);

    const begin = kind => {
        if (
            mutationOwner.current ||
            mode !== workflowModes.CONFIGURED ||
            !latest.current.open ||
            !latest.current.fileId ||
            (promotion && kind !== 'promote')
        )
            return null;
        const owner = { generation: generation.current, fileId: latest.current.fileId, kind };
        mutationOwner.current = owner;
        mutationSource.current = cancelToken.source();
        owner.token = mutationSource.current.token;
        setBusy(kind);
        return owner;
    };
    const owns = owner => mutationOwner.current === owner && current(owner);
    const finish = owner => {
        if (mutationOwner.current === owner) {
            mutationOwner.current = null;
            if (current(owner)) setBusy(null);
        }
    };
    const disablePanel = message => {
        setPhase('editing');
        frozenPromotion.current = null;
        setPromotion(null);
        setPairs([]);
        setSettings(null);
        setMode(workflowModes.DISABLED);
        setBanner(message);
    };
    const close = () => {
        if (mutationOwner.current) return;
        generation.current += 1;
        requestSource.current?.cancel();
        frozenPromotion.current = null;
        setPromotion(null);
        setPairs([]);
        setMode(workflowModes.LOADING);
        onClose();
    };

    const addSource = async () => {
        if (mutationOwner.current || promotion) return;
        const revision = normalizeRevision(newRevision);
        if (!isValidRevision(revision)) {
            setErrors({ source_revision: 'Rewizja musi mieć 1 do 16 bajtów w zapisie szesnastkowym' });
            return;
        }
        const owner = begin('add');
        if (!owner) return;
        setErrors({});
        try {
            await axiosInstance.post(
                `${endpoint.FW_FILES}/${owner.fileId}/sources`,
                { source_revision: revision },
                { headers: { 'Idempotency-Key': addKey.current }, cancelToken: owner.token }
            );
            if (!owns(owner)) return;
            setNewRevision('');
            addKey.current = operationKey();
            showMessage('Źródło dodane', snackbarVariant.SUCCESS);
            finish(owner);
            await load();
        } catch (error) {
            if (!owns(owner) || isCancelled(error)) return;
            const refusal = refusalOf(error);
            if (isDisabledRefusal(refusal)) {
                disablePanel(refusalText(refusal, copy.readError));
                return;
            }
            const serverErrors = fieldErrors(refusal);
            setErrors({
                ...serverErrors,
                source_revision: serverErrors.revision ?? serverErrors.source_revision,
            });
            setBanner(
                refusal.status === 409
                    ? 'To źródło jest już dodane do tego artefaktu'
                    : refusalText(refusal, 'Nie udało się dodać źródła')
            );
        } finally {
            finish(owner);
        }
    };

    const retirePair = async pair => {
        if (mutationOwner.current || promotion || !window.confirm(`Wycofać źródło ${pair.source_revision}?`))
            return;
        const owner = begin('retire');
        if (!owner) return;
        try {
            await axiosInstance.delete(`${endpoint.FW_PAIRS}/${pair.id}`, {
                data: { expected_version: pair.version },
                headers: { 'Idempotency-Key': operationKey() },
                cancelToken: owner.token,
            });
            if (!owns(owner)) return;
            showMessage('Źródło wycofane', snackbarVariant.SUCCESS);
            finish(owner);
            await load();
        } catch (error) {
            if (!owns(owner) || isCancelled(error)) return;
            const refusal = refusalOf(error);
            if (isDisabledRefusal(refusal)) {
                disablePanel(refusalText(refusal, copy.readError));
                return;
            }
            setBanner(
                refusal.status === 409
                    ? 'Źródło zmieniło się w innym miejscu. Odśwież listę.'
                    : refusalText(refusal, 'Nie udało się wycofać źródła')
            );
        } finally {
            finish(owner);
        }
    };

    const choosePromotion = pair => {
        if (mutationOwner.current || promotion || !promotionState(pair, settings, fileId).eligible) return;
        setOutcome(null);
        setBanner(null);
        setPromotion({ ...pair });
        setPhase('editing');
        frozenPromotion.current = null;
    };
    const sendPromotion = async () => {
        if (mutationOwner.current) return;
        if (!frozenPromotion.current) {
            const state = promotion && promotionState(promotion, settings, fileId);
            if (phase !== 'editing' || !state?.eligible) return;
            frozenPromotion.current = Object.freeze({
                fileId,
                pairId: identifier(promotion.id),
                version: promotion.version,
                target: state.target,
                key: operationKey(),
            });
        } else if (phase !== 'uncertain') return;
        const operation = frozenPromotion.current;
        if (operation.fileId !== latest.current.fileId) return;
        const owner = begin('promote');
        if (!owner) return;
        setPhase('sending');
        setBanner(null);
        try {
            const response = await axiosInstance.post(
                `${endpoint.FW_PAIRS}/${operation.pairId}/promote`,
                { expected_version: operation.version, target_stage: operation.target },
                { headers: { 'Idempotency-Key': operation.key }, cancelToken: owner.token }
            );
            if (!owns(owner)) return;
            const result = response?.data;
            const success =
                result?.code === 'FW_OPERATION_RECORDED' && result?.data?.reason === 'PAIR_PROMOTED';
            if (success || result?.code === 'FW_ADMISSION_DEFERRED') {
                const reason = result?.data?.reason;
                setOutcome(
                    success
                        ? copy.success
                        : (copy.reasons[reason] ?? (typeof reason === 'string' ? reason : copy.unknown))
                );
                finish(owner);
                await load();
            } else {
                setPhase('blocked');
                setPairs([]);
                setBanner(copy.unknown);
            }
        } catch (error) {
            if (!owns(owner) || isCancelled(error)) return;
            const refusal = refusalOf(error);
            const message = promotionRefusal(error);
            if (isDisabledRefusal(refusal)) {
                disablePanel(message);
            } else if (refusal.status === null || refusal.status >= 500) {
                setPhase('uncertain');
                setBanner(`${message} ${copy.uncertain}`);
            } else {
                setPhase('blocked');
                setPairs([]);
                setBanner(`${message} ${copy.reloadRequired}`);
            }
        } finally {
            finish(owner);
        }
    };

    const capability = file?.capability;
    const configured = mode === workflowModes.CONFIGURED;
    const selectedState = promotion ? promotionState(promotion, settings, fileId) : null;

    return (
        <Dialog
            open={open}
            onClose={close}
            disableEscapeKeyDown={Boolean(busy)}
            aria-labelledby="fw-source-title"
            maxWidth="md"
            fullWidth>
            <DialogTitle id="fw-source-title">{`Źródła — ${file?.filename ?? ''}`}</DialogTitle>
            <DialogContent>
                <Typography className={classes.summary}>
                    {'Rodzina ' +
                        (file?.family ?? '—') +
                        ', rewizja docelowa ' +
                        (file?.target_revision ?? '—') +
                        '. '}
                    {capability?.transfer_enabled
                        ? 'Transfer dozwolony.'
                        : 'Transfer zablokowany: ' +
                          (fwCapabilityReasonLabels[capability?.reason] ?? 'powód nieznany') +
                          '.'}
                </Typography>
                <Typography className={classes.summary} variant="caption">
                    {`SHA-256: ${file?.content_sha256 ?? ''}`}
                </Typography>
                <Typography className={classes.summary}>{copy.bounds}</Typography>
                {mode === workflowModes.LOADING && <Typography role="status">{copy.loading}</Typography>}
                {outcome && <Typography role="status">{outcome}</Typography>}
                {promotion ? (
                    <div role="region" aria-labelledby="fw-promotion-title">
                        <Typography variant="h6" id="fw-promotion-title">
                            {copy.confirm}
                        </Typography>
                        <Typography>
                            {copy.source +
                                ': ' +
                                promotion.source_revision +
                                '; ' +
                                copy.pair +
                                ': ' +
                                promotion.id}
                        </Typography>
                        <Typography>
                            {fwPairStageLabels[promotion.stage] +
                                ' → ' +
                                fwPairStageLabels[selectedState?.target ?? frozenPromotion.current?.target]}
                        </Typography>
                        <Typography>
                            {copy.version +
                                ': ' +
                                promotion.version +
                                '; ' +
                                copy.epoch +
                                ': ' +
                                promotion.stage_epoch +
                                '; ' +
                                copy.confirmed +
                                ': ' +
                                promotion.confirmed_count +
                                '; ' +
                                copy.outstanding +
                                ': ' +
                                promotion.outstanding_count +
                                '; ' +
                                copy.threshold +
                                ': ' +
                                selectedState?.threshold}
                        </Typography>
                        <Typography className={classes.summary}>{copy.effect}</Typography>
                    </div>
                ) : (
                    configured && (
                        <>
                            <div className={classes.table}>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            {[
                                                'Rewizja przed wymianą FW',
                                                'Etap',
                                                'Epoka etapu',
                                                'Potwierdzone sukcesy',
                                                'Nierozstrzygnięte próby',
                                                'Wersja',
                                                'Promowano',
                                                'Wycofano',
                                                '',
                                            ].map((label, index) => (
                                                <TableCell key={label || index}>{label}</TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {pairs.map(pair => {
                                            const state = promotionState(pair, settings, fileId);
                                            return (
                                                <TableRow key={pair.id}>
                                                    <TableCell>{pair.source_revision}</TableCell>
                                                    <TableCell>
                                                        {fwPairStageLabels[pair.stage] ?? pair.stage}
                                                    </TableCell>
                                                    <TableCell>{pair.stage_epoch}</TableCell>
                                                    <TableCell>{pair.confirmed_count}</TableCell>
                                                    <TableCell>{pair.outstanding_count}</TableCell>
                                                    <TableCell>{pair.version}</TableCell>
                                                    <TableCell>{formatFwDate(pair.promoted_at)}</TableCell>
                                                    <TableCell>{formatFwDate(pair.retired_at)}</TableCell>
                                                    <TableCell>
                                                        {pair.retired_at ? (
                                                            'Wycofane'
                                                        ) : (
                                                            <Button
                                                                size="small"
                                                                color="secondary"
                                                                disabled={Boolean(busy)}
                                                                onClick={() => retirePair(pair)}>
                                                                Wycofaj
                                                            </Button>
                                                        )}
                                                        {state.eligible && (
                                                            <Button
                                                                size="small"
                                                                disabled={Boolean(busy)}
                                                                onClick={() => choosePromotion(pair)}>
                                                                {copy.action +
                                                                    fwPairStageLabels[state.target]}
                                                            </Button>
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                        {pairs.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={9}>
                                                    Brak źródeł dla tego artefaktu
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                            {pairs.map(pair => (
                                <Typography key={pair.id} className={classes.promotion} variant="body2">
                                    {pair.source_revision +
                                        ': ' +
                                        promotionState(pair, settings, fileId).text}
                                </Typography>
                            ))}
                            <div className={classes.addRow}>
                                <TextField
                                    id="fw-source-new-revision"
                                    className={classes.revisionInput}
                                    label="Nowa rewizja źródła"
                                    value={newRevision}
                                    disabled={Boolean(busy)}
                                    onChange={event => {
                                        addKey.current = operationKey();
                                        setNewRevision(event.target.value);
                                    }}
                                    error={Boolean(errors.source_revision)}
                                    helperText={errors.source_revision || 'Na przykład 01 lub A1B2'}
                                    variant="outlined"
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    disabled={Boolean(busy)}
                                    onClick={addSource}>
                                    Dodaj źródło
                                </Button>
                            </div>
                        </>
                    )
                )}
                {banner && (
                    <Typography className={classes.banner} color="error" role="alert">
                        {banner}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                {promotion && phase === 'editing' && (
                    <Button
                        onClick={() => {
                            setPromotion(null);
                            frozenPromotion.current = null;
                            setBanner(null);
                        }}>
                        {copy.cancel}
                    </Button>
                )}
                {promotion && (phase === 'editing' || phase === 'sending') && (
                    <Button disabled={Boolean(busy)} onClick={sendPromotion}>
                        {copy.confirm}
                    </Button>
                )}
                {promotion && phase === 'uncertain' && (
                    <Button disabled={Boolean(busy)} onClick={sendPromotion}>
                        {copy.retry}
                    </Button>
                )}
                {(!promotion || phase === 'uncertain' || phase === 'blocked') && (
                    <Button disabled={Boolean(busy) || mode === workflowModes.LOADING} onClick={load}>
                        {copy.reload}
                    </Button>
                )}
                <Button disabled={Boolean(busy)} onClick={close}>
                    Zamknij
                </Button>
            </DialogActions>
            <Snackbar snackbarData={snackbarData} handleIsSnackbarOpen={handleIsSnackbarOpen} />
        </Dialog>
    );
};

export default withStyles(styles)(FwFileSources);
